import api from './api';
import {
  ContextSource,
  Signal,
  SignalCategory,
  ReadinessBreakdown,
  LiveSuggestion,
} from '../types/context.types';
import { Primer } from './primerService';

interface UploadSourceResponse {
  source: ContextSource;
  signals: Signal[];
}

interface GetSourcesResponse {
  sources: ContextSource[];
}

interface GetSignalsResponse {
  signals: Signal[];
}

interface CreateSignalResponse {
  signal: Signal;
}

interface UpdateSignalResponse {
  signal: Signal;
}

interface ToggleSignalResponse {
  signal: Signal;
}

interface ReadinessResponse {
  score: number;
  breakdown: ReadinessBreakdown;
}

interface SuggestionsResponse {
  suggestions: LiveSuggestion[];
}

interface GhostTextResponse {
  suggestion: string;
  signalsUsed: string[];
}

interface TemplateQuestionsResponse {
  questions: string[];
  signalsUsed: string[];
}

interface OnboardingProcessResponse {
  sources: ContextSource[];
  signals: Signal[];
  readiness: {
    score: number;
    breakdown: ReadinessBreakdown;
    suggestions: LiveSuggestion[];
  };
  primer?: Primer;
}

class ContextService {
  // --- Sources ---

  async uploadSource(
    projectId: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    data: string
  ): Promise<UploadSourceResponse> {
    // Source upload involves Claude API calls for text extraction + signal extraction
    return api.post<UploadSourceResponse>('/api/context/sources/upload', {
      projectId,
      fileName,
      fileType,
      fileSize,
      data,
    }, { timeout: 120000 });
  }

  async getSources(projectId: string): Promise<ContextSource[]> {
    const result = await api.get<GetSourcesResponse>(`/api/context/sources/${projectId}`);
    return result.sources;
  }

  async deleteSource(sourceId: string): Promise<void> {
    await api.delete(`/api/context/sources/${sourceId}`);
  }

  // --- Signals ---

  async getSignals(projectId: string): Promise<Signal[]> {
    const result = await api.get<GetSignalsResponse>(`/api/context/signals/${projectId}`);
    return result.signals;
  }

  async createManualSignal(
    projectId: string,
    label: string,
    category?: SignalCategory
  ): Promise<Signal> {
    const result = await api.post<CreateSignalResponse>('/api/context/signals', {
      projectId,
      label,
      category,
    });
    return result.signal;
  }

  async updateSignal(
    signalId: string,
    updates: { label?: string; category?: SignalCategory; description?: string }
  ): Promise<Signal> {
    const result = await api.put<UpdateSignalResponse>(`/api/context/signals/${signalId}`, updates);
    return result.signal;
  }

  async deleteSignal(signalId: string): Promise<void> {
    await api.delete(`/api/context/signals/${signalId}`);
  }

  async toggleSignal(signalId: string): Promise<Signal> {
    const result = await api.put<ToggleSignalResponse>(`/api/context/signals/${signalId}/toggle`);
    return result.signal;
  }

  // --- Readiness ---

  async getReadiness(projectId: string): Promise<ReadinessResponse> {
    return api.get<ReadinessResponse>(`/api/context/readiness/${projectId}`);
  }

  // --- Suggestions ---

  async getSuggestions(projectId: string): Promise<LiveSuggestion[]> {
    const result = await api.get<SuggestionsResponse>(`/api/context/suggestions/${projectId}`);
    return result.suggestions;
  }

  // --- Ghost Text ---

  async getGhostText(projectId: string, templateId: string): Promise<GhostTextResponse> {
    return api.post<GhostTextResponse>('/api/context/ghost-text', {
      projectId,
      templateId,
    });
  }

  // --- Template Questions ---

  async getTemplateQuestions(
    projectId: string,
    templateId: string
  ): Promise<TemplateQuestionsResponse> {
    return api.post<TemplateQuestionsResponse>('/api/context/template-questions', {
      projectId,
      templateId,
    });
  }

  // --- Onboarding ---

  async processOnboarding(
    projectId: string,
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>,
    manualTags?: Array<{ label: string; category: SignalCategory }>
  ): Promise<OnboardingProcessResponse> {
    // Onboarding involves multiple Claude API calls (per-file text extraction + batch
    // signal extraction). Even with parallel file processing on the backend, this can
    // take a few minutes for large PDFs, so the timeout matches the Cloud Run request
    // timeout (300s) rather than the default 30s.
    return api.post<OnboardingProcessResponse>('/api/context/onboarding/process', {
      projectId,
      files,
      manualTags,
    }, { timeout: 300000 });
  }

  async markOnboardingComplete(): Promise<void> {
    await api.post('/api/context/onboarding/complete');
  }
}

const contextService = new ContextService();
export default contextService;
